// src/components/layout/MenuRenderer.jsx
// Menu digital professionnel : en-tête courbe + hero, recherche, catégories en pastilles,
// plats populaires, cartes de plats (liste / grille / compact), panier, commande, suivi,
// "Appeler le serveur" / "Demander l'addition", infos pratiques, FR/EN/AR.
//
// Tout le style vient de tokens (lib/menuDesign.js) → fonctionne avec n'importe quel design.
// Props : page, preview, onOrder(payload)→{id,number}, onTrack(id)→{status}, onServiceRequest({type,table})

import { Fragment, useEffect, useMemo, useRef, useState } from 'react'
import {
  Search, Plus, Minus, ShoppingBag, X, Trash2, Send, Check, Loader2, Menu as MenuIcon,
  ChevronDown, ChevronRight, Star, UtensilsCrossed, Bell, Receipt, MoreHorizontal,
  Phone, MapPin, Clock, Wifi, AtSign, Flame, Globe,
} from 'lucide-react'
import { FONTS } from '../../lib/themeConfig'
import { formatPrice } from '../../lib/menuReviewConfig'
import { normalizeSettings, extractCategories } from '../../lib/normalizeSettings'
import { resolveDesign, designVars } from '../../lib/menuDesign'
import { UI, LANGS, textDir } from '../../lib/menuI18n'

const TAG_LABELS = {
  fr: { vegetarian: 'Végétarien', vegan: 'Vegan', spicy: 'Épicé', 'gluten-free': 'Sans gluten', halal: 'Halal', popular: 'Populaire', new: 'Nouveau' },
  en: { vegetarian: 'Vegetarian', vegan: 'Vegan', spicy: 'Spicy', 'gluten-free': 'Gluten-free', halal: 'Halal', popular: 'Popular', new: 'New' },
  ar: { vegetarian: 'نباتي', vegan: 'نباتي صرف', spicy: 'حار', 'gluten-free': 'خالٍ من الغلوتين', halal: 'حلال', popular: 'شائع', new: 'جديد' },
}
const FILTER_TAGS = ['vegetarian', 'vegan', 'spicy', 'gluten-free', 'halal']
const STEPS = ['new', 'preparing', 'ready', 'done']

/* ───────── helpers ───────── */
const toNumber = (p) => {
  const n = parseFloat(String(p ?? '').replace(',', '.').replace(/[^\d.]/g, ''))
  return Number.isFinite(n) ? n : null
}
const fmt = (n, currency) => `${Number.isInteger(n) ? n : n.toFixed(2)} ${currency}`
const vibrate = (ms = 10) => { try { navigator.vibrate?.(ms) } catch { /* ignore */ } }
const safeJson = (raw, fallback) => { try { return JSON.parse(raw) ?? fallback } catch { return fallback } }

/* ───────── animations ───────── */
const CSS = `
@keyframes mr-up   { from { opacity:0; transform:translateY(12px) } to { opacity:1; transform:none } }
@keyframes mr-fade { from { opacity:0 } to { opacity:1 } }
@keyframes mr-sheet{ from { transform:translateY(100%) } to { transform:none } }
@keyframes mr-pop  { 0%{transform:scale(.6)} 60%{transform:scale(1.15)} 100%{transform:scale(1)} }
@keyframes mr-bar  { from { transform:translateY(130%) } to { transform:none } }
@keyframes mr-toast{ 0%{opacity:0;transform:translateY(8px)} 12%,85%{opacity:1;transform:none} 100%{opacity:0;transform:translateY(-6px)} }
.mr-item  { animation: mr-up .4s cubic-bezier(.2,.7,.2,1) both }
.mr-fade  { animation: mr-fade .2s ease both }
.mr-sheet { animation: mr-sheet .32s cubic-bezier(.2,.8,.2,1) both }
.mr-pop   { animation: mr-pop .3s ease both }
.mr-bar   { animation: mr-bar .35s cubic-bezier(.2,.8,.2,1) both }
.mr-toast { animation: mr-toast 1.9s ease both }
.mr-press { transition: transform .12s ease }
.mr-press:active { transform: scale(.93) }
.mr-hide-scroll::-webkit-scrollbar { display:none }
.mr-clamp2 { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden }
@media (prefers-reduced-motion: reduce) {
  .mr-item,.mr-fade,.mr-sheet,.mr-pop,.mr-bar,.mr-toast { animation:none !important }
}
`

/* ───────── petits composants ───────── */
function Dish({ item, size = 'w-full h-full' }) {
  return item.image ? (
    <img src={item.image} alt="" loading="lazy" className={`${size} object-cover`} />
  ) : (
    <div className={`${size} flex items-center justify-center`} style={{ background: 'var(--mr-chip)', color: 'var(--mr-muted)' }}>
      <UtensilsCrossed className="w-6 h-6 opacity-60" />
    </div>
  )
}

function PopularBadge({ label }) {
  return (
    <span className="absolute top-1.5 left-1.5 flex items-center gap-1 text-[10px] font-bold px-2 py-0.5 rounded-full shadow"
      style={{ background: 'var(--mr-badge)', color: 'var(--mr-badge-text)' }}>
      <Flame className="w-3 h-3" />{label}
    </span>
  )
}

function AddControl({ qty, onAdd, onInc, onDec, name }) {
  const base = { background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }
  if (!qty) {
    return (
      <button onClick={(e) => { e.stopPropagation(); onAdd() }} aria-label={`+ ${name}`}
        className="mr-press w-9 h-9 rounded-full flex items-center justify-center shadow-md" style={base}>
        <Plus className="w-5 h-5" />
      </button>
    )
  }
  return (
    <div onClick={(e) => e.stopPropagation()} className="mr-pop flex items-center gap-0.5 rounded-full h-9 px-1" style={base}>
      <button onClick={onDec} aria-label="−" className="mr-press w-7 h-7 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
      <span key={qty} className="mr-pop text-sm font-bold w-5 text-center">{qty}</span>
      <button onClick={onInc} aria-label="+" className="mr-press w-7 h-7 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
    </div>
  )
}

function Sheet({ onClose, children, label }) {
  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center">
      <div className="mr-fade absolute inset-0 bg-black/55" onClick={onClose} />
      <div role="dialog" aria-modal="true" aria-label={label}
        className="mr-sheet relative w-full max-w-md max-h-[90%] overflow-y-auto rounded-t-3xl shadow-2xl"
        style={{ background: 'var(--mr-bg)', color: 'var(--mr-text)' }}>
        {children}
      </div>
    </div>
  )
}

/* ───────── composant principal ───────── */
export default function MenuRenderer({ page, preview = false, onOrder, onTrack, onServiceRequest }) {
  const [query, setQuery] = useState('')
  const [activeTags, setActiveTags] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [cart, setCart] = useState({})
  const [detail, setDetail] = useState(null)
  const [detailQty, setDetailQty] = useState(1)
  const [detailNote, setDetailNote] = useState('')
  const [cartOpen, setCartOpen] = useState(false)
  const [moreOpen, setMoreOpen] = useState(false)
  const [langOpen, setLangOpen] = useState(false)
  const [tablePrompt, setTablePrompt] = useState(null) // 'waiter' | 'bill'
  const [orderInfo, setOrderInfo] = useState(() => ({
    table: typeof window !== 'undefined' && !preview ? new URLSearchParams(window.location.search).get('table') || '' : '',
    name: '', note: '',
  }))
  const [order, setOrder] = useState(null)
  const [orderOpen, setOrderOpen] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [orderError, setOrderError] = useState('')
  const [toast, setToast] = useState(null)
  const [lang, setLang] = useState(() => (typeof window !== 'undefined' && localStorage.getItem('tapio-lang')) || 'fr')
  const navRef = useRef(null)
  const cooldown = useRef({})

  const settings = useMemo(() => normalizeSettings(page?.settings), [page])
  const allCategories = useMemo(() => (page ? extractCategories(page) : []), [page])
  const design = useMemo(() => resolveDesign(settings), [settings])

  const languages = useMemo(() => {
    const l = (Array.isArray(settings.languages) ? settings.languages : ['fr']).filter((x) => UI[x])
    return l.length ? l : ['fr']
  }, [settings.languages])
  const curLang = languages.includes(lang) ? lang : languages[0]
  const t = { ...UI.fr, ...UI[curLang] }
  const tagLabels = TAG_LABELS[curLang] || TAG_LABELS.fr
  const tr = (obj, field) => obj?.i18n?.[curLang]?.[field] || obj?.[field] || ''

  const storageKey = `tapio-cart-${page?.slug || page?.id || page?.title || 'menu'}`
  const orderKey = `tapio-order-${page?.slug || page?.id || 'menu'}`

  // Panier + dernière commande (public uniquement)
  useEffect(() => {
    if (preview) return
    const saved = safeJson(localStorage.getItem(storageKey), {})
    if (saved && typeof saved === 'object') setCart(saved)
    const last = safeJson(localStorage.getItem(orderKey), null)
    if (last?.id && Date.now() - (last.ts || 0) < 8 * 3600 * 1000 && !['done', 'cancelled'].includes(last.status)) setOrder(last)
  }, [storageKey, orderKey, preview])

  useEffect(() => {
    if (preview) return
    try { localStorage.setItem(storageKey, JSON.stringify(cart)) } catch { /* ignore */ }
  }, [cart, storageKey, preview])

  useEffect(() => {
    if (preview || !order?.id) return
    try { localStorage.setItem(orderKey, JSON.stringify({ id: order.id, number: order.number, status: order.status, ts: order.ts })) } catch { /* ignore */ }
  }, [order, orderKey, preview])

  useEffect(() => {
    try { localStorage.setItem('tapio-lang', curLang) } catch { /* ignore */ }
  }, [curLang])

  // Suivi de la commande
  useEffect(() => {
    if (!order?.id || !onTrack || ['done', 'cancelled'].includes(order.status)) return
    let stop = false
    const tick = async () => {
      try {
        const r = await onTrack(order.id)
        if (!stop && r?.status) setOrder((c) => (c && c.id === order.id ? { ...c, status: r.status } : c))
      } catch { /* retry */ }
    }
    const timer = setInterval(tick, 8000)
    return () => { stop = true; clearInterval(timer) }
  }, [order?.id, order?.status, onTrack])

  useEffect(() => {
    if (!toast) return
    const timer = setTimeout(() => setToast(null), 1900)
    return () => clearTimeout(timer)
  }, [toast])

  /* ── données dérivées ── */
  const popularItems = useMemo(
    () => allCategories.flatMap((c) => c.items).filter((i) => Array.isArray(i.tags) && i.tags.includes('popular')),
    [allCategories],
  )

  const availableTags = useMemo(
    () => FILTER_TAGS.filter((tg) => allCategories.some((c) => c.items.some((i) => Array.isArray(i.tags) && i.tags.includes(tg)))),
    [allCategories],
  )

  const filtering = query.trim() !== '' || activeTags.length > 0

  const categories = useMemo(() => {
    const q = query.trim().toLowerCase()
    return allCategories
      .map((c) => ({
        ...c,
        items: c.items.filter((i) =>
          (!q || `${tr(i, 'name')} ${tr(i, 'description')} ${tr(c, 'name')}`.toLowerCase().includes(q)) &&
          activeTags.every((tg) => (i.tags || []).includes(tg))),
      }))
      .filter((c) => c.items.length)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allCategories, query, activeTags, curLang])

  const sections = useMemo(() => {
    const pop = !filtering && popularItems.length
      ? [{ id: 'popular', name: t.popularTitle, items: popularItems, virtual: true }]
      : []
    return [...pop, ...categories]
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categories, popularItems, filtering, curLang])

  const chips = useMemo(
    () => sections.map((s) => ({
      id: s.id,
      label: s.virtual ? t.popular : tr(s, 'name'),
      icon: s.virtual ? Star : null,
      image: s.image || s.items.find((i) => i.image)?.image || '',
    })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sections, curLang],
  )

  // Catégorie active pendant le scroll
  const sectionKey = sections.map((s) => s.id).join('|')
  useEffect(() => {
    if (sections.length < 2 || typeof IntersectionObserver === 'undefined') return
    const els = sections.map((s) => document.getElementById(`menu-cat-${s.id}`)).filter(Boolean)
    const io = new IntersectionObserver((entries) => {
      const vis = entries.filter((e) => e.isIntersecting).sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
      if (vis) setActiveId(vis.target.id.replace('menu-cat-', ''))
    }, { rootMargin: '-25% 0px -65% 0px' })
    els.forEach((el) => io.observe(el))
    return () => io.disconnect()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionKey])

  useEffect(() => {
    navRef.current?.querySelector('[data-active="true"]')?.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' })
  }, [activeId])

  if (!page) return null

  const font = FONTS.find((f) => f.id === settings.font) || FONTS[0]
  const currency = settings.currency || 'DH'
  const logo = page.avatar || page.logo
  const totalItems = allCategories.reduce((n, c) => n + c.items.length, 0)
  const showSearch = totalItems > 5
  const orderEnabled = settings.orderEnabled !== false
  const whatsapp = String(settings.whatsapp || '').replace(/\D/g, '')
  const askTable = settings.askTable !== false
  const itemLayout = ['list', 'grid', 'compact'].includes(settings.itemLayout) ? settings.itemLayout : 'list'
  const hero = settings.heroImage
  const hasInfo = ['infoPhone', 'infoAddress', 'infoHours', 'infoWifi', 'infoInstagram'].some((k) => settings[k])

  /* ── panier ── */
  const lines = Object.values(cart)
  const count = lines.reduce((n, l) => n + l.qty, 0)
  const total = lines.reduce((s, l) => s + (toNumber(l.item.price) || 0) * l.qty, 0)
  const orderActive = order && !['done', 'cancelled'].includes(order.status)

  const notify = (msg) => setToast({ msg, k: Date.now() })

  const setQty = (item, qty, note) =>
    setCart((cur) => {
      const next = { ...cur }
      if (qty <= 0) delete next[item.id]
      else next[item.id] = { item, qty, note: note ?? cur[item.id]?.note ?? '' }
      return next
    })
  const add = (item) => { vibrate(); setQty(item, (cart[item.id]?.qty || 0) + 1); notify(`${tr(item, 'name')} ${t.added}`) }
  const inc = (item) => { vibrate(); setQty(item, (cart[item.id]?.qty || 0) + 1) }
  const dec = (item) => { vibrate(); setQty(item, (cart[item.id]?.qty || 0) - 1) }

  const goTo = (id) => document.getElementById(`menu-cat-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  const toggleTag = (tg) => setActiveTags((cur) => (cur.includes(tg) ? cur.filter((x) => x !== tg) : [...cur, tg]))

  const openDetail = (item) => {
    setDetail(item)
    setDetailQty(cart[item.id]?.qty || 1)
    setDetailNote(cart[item.id]?.note || '')
  }

  /* ── appeler le serveur / addition ── */
  const requestService = async (type, tableOverride) => {
    if (preview) { notify(t.previewOnly); return }
    const table = (tableOverride ?? orderInfo.table).trim()
    if (!table) { setTablePrompt(type); return }
    if ((cooldown.current[type] || 0) > Date.now()) { notify(t.alreadySent); return }
    try {
      await onServiceRequest?.({ type, table })
      cooldown.current[type] = Date.now() + 60000
      vibrate(20)
      notify(type === 'bill' ? t.billSent : t.waiterSent)
    } catch {
      notify(t.error)
    }
  }

  /* ── commande ── */
  const buildMessage = () => {
    const rows = lines.map((l) => {
      const p = toNumber(l.item.price)
      return `• ${l.qty} × ${l.item.name}${p != null ? ` — ${fmt(p * l.qty, currency)}` : ''}${l.note ? `\n   ↳ ${l.note}` : ''}`
    })
    return [
      `🧾 ${page.title}`,
      orderInfo.table ? `${UI.fr.table} : ${orderInfo.table}` : null,
      orderInfo.name ? `Nom : ${orderInfo.name}` : null,
      '', ...rows, '', `Total : ${fmt(total, currency)}`,
      orderInfo.note ? `Remarque : ${orderInfo.note}` : null,
    ].filter((x) => x !== null).join('\n')
  }

  const sendOrder = async () => {
    if (!lines.length || submitting) return
    setOrderError('')
    setSubmitting(true)
    const message = buildMessage()
    let saved = null

    if (onOrder && !preview) {
      try {
        saved = await onOrder({
          table: orderInfo.table.trim(), name: orderInfo.name.trim(), note: orderInfo.note.trim(), locale: curLang,
          items: lines.map((l) => ({ id: l.item.id, qty: l.qty, note: l.note || '' })),
        })
      } catch (err) {
        if (!whatsapp) {
          setOrderError(err?.response?.data?.error || err?.response?.data?.message || t.error)
          setSubmitting(false)
          return
        }
      }
    }
    if (!saved && whatsapp && !preview) {
      window.open(`https://wa.me/${whatsapp}?text=${encodeURIComponent(message)}`, '_blank', 'noopener')
    }

    setOrder({
      id: saved?.id, number: saved?.number, status: saved ? 'new' : null, ts: Date.now(),
      lines, total, table: orderInfo.table, viaWhatsapp: !saved && !!whatsapp,
    })
    setOrderOpen(true)
    setCartOpen(false)
    setCart({})
    setSubmitting(false)
  }

  const statusLabel = (s) => t[`status${s.charAt(0).toUpperCase()}${s.slice(1)}`] || s
  const field = 'w-full px-3 py-2.5 rounded-xl text-sm bg-transparent outline-none border'
  const fieldStyle = { borderColor: 'var(--mr-border)', background: 'var(--mr-surface)' }
  const mutedStyle = { color: 'var(--mr-muted)' }

  /* ── cartes de plats ── */
  const renderItem = (item, index) => {
    const orderable = orderEnabled && toNumber(item.price) !== null
    const qty = cart[item.id]?.qty || 0
    const isPopular = Array.isArray(item.tags) && item.tags.includes('popular')
    const control = orderable && (
      <AddControl qty={qty} onAdd={() => add(item)} onInc={() => inc(item)} onDec={() => dec(item)} name={tr(item, 'name')} />
    )
    const price = item.price ? (
      <span className="text-[15px] font-extrabold whitespace-nowrap" style={{ color: 'var(--mr-price)' }}>
        {formatPrice(item.price, currency)}
      </span>
    ) : null
    const card = {
      background: 'var(--mr-surface)', borderRadius: 'var(--mr-radius)', boxShadow: 'var(--mr-shadow)',
      border: '1px solid var(--mr-border)', animationDelay: `${Math.min(index, 8) * 40}ms`,
    }
    const nameEl = <h3 className="text-[15px] font-bold leading-tight">{tr(item, 'name')}</h3>
    const descEl = item.description ? <p className="text-xs mt-1 mr-clamp2" style={mutedStyle}>{tr(item, 'description')}</p> : null

    if (itemLayout === 'grid') {
      return (
        <article className="mr-item flex flex-col overflow-hidden cursor-pointer" style={card} onClick={() => openDetail(item)}>
          <div className="relative h-28 w-full"><Dish item={item} />{isPopular && <PopularBadge label={t.popularBadge} />}</div>
          <div className="p-2.5 flex flex-col flex-1 gap-1">
            {nameEl}{descEl}
            <div className="mt-auto pt-1.5 flex items-center justify-between gap-2">{price}{control}</div>
          </div>
        </article>
      )
    }

    if (itemLayout === 'compact') {
      return (
        <article className="mr-item flex items-center gap-3 px-3 py-2.5 cursor-pointer" style={card} onClick={() => openDetail(item)}>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2">{nameEl}{price}</div>
            {descEl}
          </div>
          {control}
        </article>
      )
    }

    return (
      <article className="mr-item flex gap-3 p-2.5 cursor-pointer" style={card} onClick={() => openDetail(item)}>
        <div className="relative shrink-0 w-[100px] h-[100px] overflow-hidden" style={{ borderRadius: 'calc(var(--mr-radius) - 4px)' }}>
          <Dish item={item} />{isPopular && <PopularBadge label={t.popularBadge} />}
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
          <div>{nameEl}{descEl}</div>
          {price}
        </div>
        {control && <div className="self-center shrink-0">{control}</div>}
      </article>
    )
  }

  const barBottom = 'calc(4rem + env(safe-area-inset-bottom))'

  return (
    <div
      data-renderer="MenuRenderer"
      dir={textDir(curLang)}
      className={`relative ${preview ? 'min-h-full' : 'min-h-screen'}`}
      style={{ ...designVars(design), background: design.pageBackground, color: 'var(--mr-text)', fontFamily: font.family }}
    >
      <style>{CSS}</style>

      <div className={`${preview ? '' : 'max-w-md mx-auto'} w-full min-h-full`} style={{ background: 'var(--mr-bg)', paddingBottom: '9.5rem' }}>
        {/* ═══ En-tête courbe ═══ */}
        <header className="relative z-10 px-4 pt-4 pb-4 text-center"
          style={{ background: 'var(--mr-bg)', borderRadius: '0 0 2rem 2rem', boxShadow: hero ? '0 8px 24px rgba(0,0,0,.08)' : 'none' }}>
          <div className="flex items-center justify-between">
            {languages.length > 1 ? (
              <div className="relative">
                <button onClick={() => setLangOpen((o) => !o)} aria-haspopup="listbox" aria-expanded={langOpen}
                  className="mr-press flex items-center gap-1.5 px-3 h-9 rounded-xl text-xs font-bold shadow-sm"
                  style={{ background: 'var(--mr-surface)', border: '1px solid var(--mr-border)' }}>
                  <Globe className="w-3.5 h-3.5" />{curLang.toUpperCase()}<ChevronDown className="w-3.5 h-3.5" />
                </button>
                {langOpen && (
                  <ul role="listbox" className="mr-fade absolute z-30 mt-1 min-w-[130px] rounded-xl overflow-hidden shadow-xl text-start"
                    style={{ background: 'var(--mr-surface)', border: '1px solid var(--mr-border)' }}>
                    {languages.map((l) => (
                      <li key={l}>
                        <button role="option" aria-selected={l === curLang} onClick={() => { setLang(l); setLangOpen(false) }}
                          className="w-full flex items-center justify-between gap-3 px-3 py-2 text-xs font-medium">
                          {LANGS[l]}{l === curLang && <Check className="w-3.5 h-3.5" style={{ color: 'var(--mr-accent)' }} />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            ) : <span className="w-9" />}
            <button onClick={() => setMoreOpen(true)} aria-label={t.tabMore} className="mr-press w-9 h-9 flex items-center justify-center">
              <MenuIcon className="w-6 h-6" />
            </button>
          </div>

          <div className="flex flex-col items-center -mt-1">
            {logo ? <img src={logo} alt="" className="h-14 w-auto max-w-[180px] object-contain mb-1.5" /> : null}
            <h1 className="text-2xl font-extrabold leading-tight" style={{ color: 'var(--mr-accent)' }}>{page.title}</h1>
            {settings.tagline && <p className="text-[11px] mt-0.5" style={mutedStyle}>{settings.tagline}</p>}
          </div>
        </header>

        {/* ═══ Hero ═══ */}
        {hero && (
          <div className="relative -mt-8 h-52 overflow-hidden">
            <img src={hero} alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, rgba(0,0,0,.55), rgba(0,0,0,.05) 70%)' }} />
            {settings.heroText && (
              <p className="absolute start-5 bottom-12 max-w-[60%] text-xl leading-snug text-white italic"
                style={{ fontFamily: 'Georgia, "Times New Roman", serif', textShadow: '0 2px 10px rgba(0,0,0,.5)' }}>
                {settings.heroText}
              </p>
            )}
          </div>
        )}

        {/* ═══ Corps ═══ */}
        <div className={`relative px-4 pt-4 ${hero ? '-mt-6' : ''}`}
          style={{ background: 'var(--mr-bg)', borderRadius: hero ? '1.75rem 1.75rem 0 0' : 0 }}>
          {showSearch && (
            <label className="flex items-center gap-2.5 px-4 h-12 rounded-full mb-3"
              style={{ background: 'var(--mr-surface)', border: '1px solid var(--mr-border)', boxShadow: 'var(--mr-shadow)' }}>
              <Search className="w-5 h-5 opacity-60" />
              <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t.search}
                aria-label={t.search} className="flex-1 bg-transparent outline-none text-sm placeholder:opacity-60" />
              {query && <button onClick={() => setQuery('')} aria-label={t.close}><X className="w-4 h-4 opacity-60" /></button>}
            </label>
          )}

          {chips.length > 1 && (
            <nav ref={navRef} aria-label="Catégories"
              className="mr-hide-scroll sticky top-0 z-20 -mx-4 px-4 py-2 flex gap-2 overflow-x-auto"
              style={{ background: 'var(--mr-bg)', scrollbarWidth: 'none' }}>
              {chips.map((c) => {
                const on = (activeId || chips[0]?.id) === c.id
                return (
                  <button key={c.id} data-active={on} onClick={() => goTo(c.id)}
                    className="mr-press shrink-0 w-[76px] flex flex-col items-center gap-1.5 py-2 px-1 rounded-2xl transition-colors duration-200"
                    style={on ? { background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }
                      : { background: 'var(--mr-chip)', color: 'var(--mr-text)' }}>
                    <span className="w-10 h-10 rounded-full overflow-hidden flex items-center justify-center text-base font-bold"
                      style={{ background: on ? 'rgba(255,255,255,.18)' : 'var(--mr-surface)' }}>
                      {c.icon ? <c.icon className="w-5 h-5" /> : c.image ? <img src={c.image} alt="" className="w-full h-full object-cover" /> : (c.label || '?').charAt(0)}
                    </span>
                    <span className="text-[11px] font-semibold leading-none truncate max-w-full">{c.label}</span>
                  </button>
                )
              })}
            </nav>
          )}

          {availableTags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1 mb-2">
              {availableTags.map((tg) => {
                const on = activeTags.includes(tg)
                return (
                  <button key={tg} onClick={() => toggleTag(tg)} aria-pressed={on}
                    className="mr-press text-[11px] px-2.5 py-1 rounded-full border transition-colors"
                    style={on ? { background: 'var(--mr-accent)', color: 'var(--mr-accent-text)', borderColor: 'var(--mr-accent)' }
                      : { borderColor: 'var(--mr-border)', color: 'var(--mr-muted)' }}>
                    {tagLabels[tg]}
                  </button>
                )
              })}
            </div>
          )}

          {allCategories.length === 0 && <p className="text-center text-sm py-10" style={mutedStyle}>{t.noDishes}</p>}
          {allCategories.length > 0 && sections.length === 0 && filtering && (
            <p className="text-center text-sm py-10" style={mutedStyle}>{t.noResults}</p>
          )}

          <div className="space-y-7 mt-3">
            {sections.map((sec) => (
              <section key={sec.id} id={`menu-cat-${sec.id}`} style={{ scrollMarginTop: chips.length > 1 ? 104 : 8 }}>
                <div className="flex items-center justify-between mb-3">
                  <h2 className="text-lg font-extrabold">{sec.virtual ? sec.name : tr(sec, 'name')}</h2>
                  {sec.virtual && categories[0] && (
                    <button onClick={() => goTo(categories[0].id)} className="flex items-center text-xs font-medium" style={mutedStyle}>
                      {t.seeAll}<ChevronRight className="w-4 h-4 rtl:rotate-180" />
                    </button>
                  )}
                </div>
                <div className={itemLayout === 'grid' ? 'grid grid-cols-2 gap-3' : 'space-y-3'}>
                  {sec.items.map((item, i) => (
                    <Fragment key={`${sec.id}-${item.id}`}>{renderItem(item, i)}</Fragment>
                  ))}
                </div>
              </section>
            ))}
          </div>

          {!preview && <div className="text-center mt-10 opacity-40 text-[11px]">Propulsé par Tapio</div>}
        </div>
      </div>

      {/* ═══ Barres flottantes ═══ */}
      <div className="fixed inset-x-0 z-30 px-3 pointer-events-none" style={{ bottom: barBottom }}>
        <div className="max-w-md mx-auto space-y-2">
          {orderActive && !orderOpen && (
            <button onClick={() => setOrderOpen(true)}
              className="mr-bar mr-press pointer-events-auto w-full flex items-center justify-between rounded-2xl px-4 py-2.5 text-xs font-semibold shadow-lg"
              style={{ background: 'var(--mr-surface)', color: 'var(--mr-text)', border: '1px solid var(--mr-border)' }}>
              <span>{t.orderNo}{order.number} · <span style={{ color: 'var(--mr-accent)' }}>{order.status ? statusLabel(order.status) : ''}</span></span>
              <span className="flex items-center" style={mutedStyle}>{t.follow}<ChevronRight className="w-4 h-4 rtl:rotate-180" /></span>
            </button>
          )}
          {orderEnabled && count > 0 && (
            <button onClick={() => setCartOpen(true)}
              className="mr-bar mr-press pointer-events-auto w-full flex items-stretch rounded-2xl shadow-xl overflow-hidden"
              style={{ background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }}>
              <span className="flex-1 flex items-center gap-3 px-4 py-3 text-start">
                <span key={count} className="mr-pop relative">
                  <ShoppingBag className="w-7 h-7" />
                  <span className="absolute -top-1.5 -end-2 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
                    style={{ background: 'var(--mr-badge)', color: 'var(--mr-badge-text)' }}>{count}</span>
                </span>
                <span className="flex flex-col leading-tight">
                  <span className="text-sm font-bold">{t.viewOrder}</span>
                  <span className="text-[11px] opacity-80">{t.articles(count)}{orderInfo.table ? ` • ${t.table} ${orderInfo.table}` : ''}</span>
                </span>
              </span>
              <span className="flex items-center gap-2 px-4 text-sm font-bold" style={{ borderInlineStart: '1px solid rgba(255,255,255,.2)' }}>
                {fmt(total, currency)}<ChevronRight className="w-4 h-4 rtl:rotate-180" />
              </span>
            </button>
          )}
        </div>
      </div>

      {/* ═══ Barre d'onglets ═══ */}
      <nav className="fixed inset-x-0 bottom-0 z-30" style={{ paddingBottom: 'env(safe-area-inset-bottom)', background: 'var(--mr-surface)', borderTop: '1px solid var(--mr-border)' }}>
        <div className="max-w-md mx-auto h-16 grid grid-cols-4">
          {[
            { id: 'menu', icon: UtensilsCrossed, label: t.tabMenu, on: () => document.querySelector('[data-renderer="MenuRenderer"]')?.scrollIntoView({ behavior: 'smooth' }), active: true },
            { id: 'waiter', icon: Bell, label: t.tabWaiter, on: () => requestService('waiter') },
            { id: 'bill', icon: Receipt, label: t.tabBill, on: () => requestService('bill') },
            { id: 'more', icon: MoreHorizontal, label: t.tabMore, on: () => setMoreOpen(true) },
          ].map((tab) => (
            <button key={tab.id} onClick={tab.on}
              className="mr-press flex flex-col items-center justify-center gap-1 text-[10.5px] font-semibold px-1"
              style={{ color: tab.active ? 'var(--mr-accent)' : 'var(--mr-muted)' }}>
              <tab.icon className="w-[22px] h-[22px]" />
              <span className="truncate max-w-full">{tab.label}</span>
            </button>
          ))}
        </div>
      </nav>

      {/* Toast */}
      {toast && (
        <div key={toast.k} className="fixed top-4 inset-x-0 z-[60] flex justify-center pointer-events-none px-4">
          <div className="mr-toast flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold shadow-lg"
            style={{ background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }}>
            <Check className="w-3.5 h-3.5" /> {toast.msg}
          </div>
        </div>
      )}

      {/* ═══ Fiche plat ═══ */}
      {detail && (
        <Sheet onClose={() => setDetail(null)} label={tr(detail, 'name')}>
          <button onClick={() => setDetail(null)} aria-label={t.close}
            className="absolute top-3 end-3 z-10 w-8 h-8 rounded-full bg-black/45 text-white flex items-center justify-center"><X className="w-4 h-4" /></button>
          {detail.image && <img src={detail.image} alt="" className="w-full h-56 object-cover" />}
          <div className="p-5 space-y-3">
            <div className="flex items-start justify-between gap-3">
              <h2 className="text-xl font-extrabold leading-tight">{tr(detail, 'name')}</h2>
              {detail.price ? <span className="text-lg font-extrabold shrink-0" style={{ color: 'var(--mr-price)' }}>{formatPrice(detail.price, currency)}</span> : null}
            </div>
            {detail.description && <p className="text-sm" style={mutedStyle}>{tr(detail, 'description')}</p>}
            {Array.isArray(detail.tags) && detail.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {detail.tags.map((tg) => (
                  <span key={tg} className="text-[11px] px-2 py-0.5 rounded-full border" style={{ borderColor: 'var(--mr-border)', color: 'var(--mr-muted)' }}>{tagLabels[tg] || tg}</span>
                ))}
              </div>
            )}
            {orderEnabled && toNumber(detail.price) !== null && (
              <>
                <input value={detailNote} onChange={(e) => setDetailNote(e.target.value)} placeholder={t.itemNote} className={field} style={fieldStyle} />
                <div className="flex items-center gap-3 pt-1">
                  <div className="flex items-center gap-3 rounded-full px-2 h-12" style={fieldStyle}>
                    <button onClick={() => setDetailQty((q) => Math.max(1, q - 1))} aria-label="−" className="mr-press w-8 h-8 flex items-center justify-center"><Minus className="w-4 h-4" /></button>
                    <span key={detailQty} className="mr-pop text-sm font-bold w-5 text-center">{detailQty}</span>
                    <button onClick={() => setDetailQty((q) => q + 1)} aria-label="+" className="mr-press w-8 h-8 flex items-center justify-center"><Plus className="w-4 h-4" /></button>
                  </div>
                  <button onClick={() => { vibrate(); setQty(detail, detailQty, detailNote.trim()); notify(`${tr(detail, 'name')} ${t.added}`); setDetail(null) }}
                    className="mr-press flex-1 h-12 rounded-full text-sm font-bold"
                    style={{ background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }}>
                    {t.add} · {fmt((toNumber(detail.price) || 0) * detailQty, currency)}
                  </button>
                </div>
              </>
            )}
          </div>
        </Sheet>
      )}

      {/* ═══ Panier ═══ */}
      {cartOpen && (
        <Sheet onClose={() => setCartOpen(false)} label={t.cart}>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-extrabold">{t.cart}</h2>
              <div className="flex items-center gap-1">
                <button onClick={() => setCart({})} className="text-xs px-2 py-1 flex items-center gap-1" style={mutedStyle}><Trash2 className="w-3.5 h-3.5" /> {t.clear}</button>
                <button onClick={() => setCartOpen(false)} aria-label={t.close} className="p-1.5"><X className="w-5 h-5" /></button>
              </div>
            </div>

            {lines.length === 0 ? (
              <p className="text-sm text-center py-8" style={mutedStyle}>{t.empty}</p>
            ) : (
              <>
                <ul className="space-y-3">
                  {lines.map(({ item, qty, note }) => (
                    <li key={item.id} className="flex items-center gap-3">
                      <div className="w-14 h-14 rounded-xl overflow-hidden shrink-0"><Dish item={item} /></div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-semibold truncate">{tr(item, 'name')}</div>
                        {note && <div className="text-[11px] truncate" style={mutedStyle}>↳ {note}</div>}
                        <div className="text-sm font-bold" style={{ color: 'var(--mr-price)' }}>{fmt((toNumber(item.price) || 0) * qty, currency)}</div>
                      </div>
                      <AddControl qty={qty} onAdd={() => add(item)} onInc={() => inc(item)} onDec={() => dec(item)} name={tr(item, 'name')} />
                    </li>
                  ))}
                </ul>

                <div className="space-y-2 pt-3" style={{ borderTop: '1px solid var(--mr-border)' }}>
                  {askTable && (
                    <input value={orderInfo.table} inputMode="numeric" onChange={(e) => setOrderInfo({ ...orderInfo, table: e.target.value })}
                      placeholder={t.tableNo} className={field} style={fieldStyle} />
                  )}
                  <input value={orderInfo.name} onChange={(e) => setOrderInfo({ ...orderInfo, name: e.target.value })} placeholder={t.yourName} className={field} style={fieldStyle} />
                  <textarea rows={2} value={orderInfo.note} onChange={(e) => setOrderInfo({ ...orderInfo, note: e.target.value })} placeholder={t.remark} className={`${field} resize-none`} style={fieldStyle} />
                </div>

                <div className="flex items-center justify-between text-base font-extrabold">
                  <span>{t.total}</span><span style={{ color: 'var(--mr-price)' }}>{fmt(total, currency)}</span>
                </div>
                {orderError && <p role="alert" className="text-xs text-red-500 text-center">{orderError}</p>}
                <button onClick={sendOrder} disabled={submitting}
                  className="mr-press w-full h-12 rounded-full text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
                  style={{ background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }}>
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 rtl:-scale-x-100" />}
                  {onOrder || !whatsapp ? t.send : t.sendWhatsapp}
                </button>
              </>
            )}
          </div>
        </Sheet>
      )}

      {/* ═══ Suivi / confirmation ═══ */}
      {order && orderOpen && (
        <Sheet onClose={() => setOrderOpen(false)} label={t.orderNo}>
          <div className="p-6 text-center space-y-4">
            <div className="mr-pop mx-auto w-14 h-14 rounded-full flex items-center justify-center" style={{ background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }}>
              <Check className="w-7 h-7" />
            </div>
            <h2 className="text-lg font-extrabold">{order.number ? `${t.orderNo}${order.number}` : t.orderReady}</h2>

            {order.status ? (
              <>
                <p key={order.status} className="mr-pop text-sm font-bold" style={{ color: 'var(--mr-accent)' }}>{statusLabel(order.status)}</p>
                {order.status !== 'cancelled' && (
                  <div className="flex items-center gap-1.5 px-4" aria-hidden>
                    {STEPS.map((s, i) => (
                      <span key={s} className="flex-1 h-1.5 rounded-full transition-colors duration-500"
                        style={{ background: i <= STEPS.indexOf(order.status) ? 'var(--mr-accent)' : 'var(--mr-chip)' }} />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm" style={mutedStyle}>{order.viaWhatsapp ? t.whatsappOpened : t.showWaiter}</p>
            )}

            <ul className="text-start text-sm space-y-1 rounded-2xl p-3" style={{ background: 'var(--mr-surface)', border: '1px solid var(--mr-border)' }}>
              {order.table && <li className="font-bold">{t.table} {order.table}</li>}
              {(order.lines || []).map((l) => (
                <li key={l.item.id} className="flex justify-between gap-2">
                  <span>{l.qty} × {tr(l.item, 'name')}</span>
                  <span style={mutedStyle}>{fmt((toNumber(l.item.price) || 0) * l.qty, currency)}</span>
                </li>
              ))}
              {order.total != null && (
                <li className="flex justify-between font-extrabold pt-1" style={{ borderTop: '1px solid var(--mr-border)' }}>
                  <span>{t.total}</span><span style={{ color: 'var(--mr-price)' }}>{fmt(order.total, currency)}</span>
                </li>
              )}
            </ul>
            <button onClick={() => setOrderOpen(false)} className="mr-press w-full h-11 rounded-full text-sm font-bold"
              style={{ background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }}>{t.backToMenu}</button>
          </div>
        </Sheet>
      )}

      {/* ═══ Plus / infos ═══ */}
      {moreOpen && (
        <Sheet onClose={() => setMoreOpen(false)} label={t.info}>
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-extrabold">{page.title}</h2>
              <button onClick={() => setMoreOpen(false)} aria-label={t.close} className="p-1.5"><X className="w-5 h-5" /></button>
            </div>
            {!hasInfo && <p className="text-sm" style={mutedStyle}>{settings.tagline || ''}</p>}
            <ul className="space-y-3 text-sm">
              {settings.infoPhone && (
                <li><a href={`tel:${String(settings.infoPhone).replace(/\s/g, '')}`} className="flex items-center gap-3"><Phone className="w-4 h-4" style={{ color: 'var(--mr-accent)' }} />{settings.infoPhone}</a></li>
              )}
              {settings.infoAddress && <li className="flex items-start gap-3"><MapPin className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--mr-accent)' }} />{settings.infoAddress}</li>}
              {settings.infoHours && <li className="flex items-start gap-3"><Clock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: 'var(--mr-accent)' }} /><span style={{ whiteSpace: 'pre-line' }}>{settings.infoHours}</span></li>}
              {settings.infoWifi && <li className="flex items-center gap-3"><Wifi className="w-4 h-4" style={{ color: 'var(--mr-accent)' }} />{t.wifi} : {settings.infoWifi}</li>}
              {settings.infoInstagram && (
                <li><a href={`https://instagram.com/${String(settings.infoInstagram).replace('@', '')}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3">
                  <AtSign className="w-4 h-4" style={{ color: 'var(--mr-accent)' }} />{settings.infoInstagram}</a></li>
              )}
            </ul>
          </div>
        </Sheet>
      )}

      {/* ═══ Numéro de table (appel serveur / addition) ═══ */}
      {tablePrompt && (
        <Sheet onClose={() => setTablePrompt(null)} label={t.askTableTitle}>
          <form className="p-5 space-y-3" onSubmit={(e) => {
            e.preventDefault()
            const value = new FormData(e.currentTarget).get('table')?.toString().trim()
            if (!value) return
            const type = tablePrompt
            setOrderInfo((o) => ({ ...o, table: value }))
            setTablePrompt(null)
            requestService(type, value)
          }}>
            <h2 className="text-lg font-extrabold">{t.askTableTitle}</h2>
            <input name="table" autoFocus inputMode="numeric" className={field} style={fieldStyle} placeholder={t.tableNo} />
            <button type="submit" className="mr-press w-full h-12 rounded-full text-sm font-bold" style={{ background: 'var(--mr-accent)', color: 'var(--mr-accent-text)' }}>{t.confirm}</button>
          </form>
        </Sheet>
      )}
    </div>
  )
}